import { useEffect, useState } from "react";
import api from "@api";
import "./SeasonList.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../../context/AuthContext.jsx";

function SeasonList() {
  const [seasons, setSeasons] = useState([]);

  const [seasonForm, setSeasonForm] = useState({
    name: "",
    startDate: "",
    finishDate: "",
    active: true,
    showDivisions: false,
    copyFromSeasonId: "",
  });

  const { user } = useAuth();

  const [updateForm, setUpdateForm] = useState({
    id: null,
    name: "",
    startDate: "",
    finishDate: "",
    active: true,
    showDivisions: false,
  });

  const [sports, setSports] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedSeasons, setSelectedSeasons] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const [selectedSportId, setSelectedSportId] = useState(
    user?.organization?.sports?.[0]?.id || user?.sports?.[0]?.id || ""
  );

  const [savingSport, setSavingSport] = useState(false);
  const [hasSavedSport, setHasSavedSport] = useState(false);
  const [orgSportId, setOrgSportId] = useState("");
  const [checkingSport, setCheckingSport] = useState(true);

  const isSuperAdmin = user?.role === "super_admin";

  const hasSportAssigned = !!orgSportId || hasSavedSport;

  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const res = await api.get("/api/seasonsv2", {
          withCredentials: true,
        });

        setSeasons(res.data.seasons || []);
      } catch (err) {
        console.error("Error fetching seasons:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSeasons();
  }, []);

  useEffect(() => {
    const fetchOrganization = async () => {
      if (!user?.organization_id) {
        setCheckingSport(false);
        return;
      }

      try {
        const res = await api.get(`/api/organizations/${user.organization_id}`, {
          withCredentials: true,
        });

        const savedSportId = res.data?.organization?.sports?.[0]?.id || "";

        setOrgSportId(savedSportId);
        setSelectedSportId(savedSportId || "");
        setHasSavedSport(!!savedSportId);
      } catch (err) {
        console.error("Error fetching organization sport:", err);
      } finally {
        setCheckingSport(false);
      }
    };

    fetchOrganization();
  }, [user?.organization_id]);

  useEffect(() => {
    const fetchSports = async () => {
      try {
        const res = await api.get("/api/sportsv2", {
          withCredentials: true,
        });

        setSports(res.data?.sports || []);
      } catch (err) {
        console.error("Error fetching sports:", err);
      }
    };

    fetchSports();
  }, []);

  const handleSaveSport = async () => {
    try {
      if (!selectedSportId) {
        alert("Please select a sport.");
        return;
      }

      setSavingSport(true);

      await api.post(
        `/api/organizations/${user.organization_id}/sports`,
        { sport_id: Number(selectedSportId) },
        { withCredentials: true }
      );

      setOrgSportId(Number(selectedSportId));
      setHasSavedSport(true);
      setMessage("Sport selected");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Save sport error:", err);
      alert(err?.response?.data?.message || "Could not save sport.");
    } finally {
      setSavingSport(false);
    }
  };

  const handleCreateSeason = async (e) => {
    e.preventDefault();

    try {
      const sport_id = selectedSportId;

      if (!sport_id) {
        alert("Please select a sport first.");
        return;
      }

      const payload = {
        ...seasonForm,
        sport_id,
      };

      const res = await api.post("/api/seasonsv2", payload, {
        withCredentials: true,
      });

      setSeasons((prev) => [...prev, res.data.season]);

      setSeasonForm({
        name: "",
        startDate: "",
        finishDate: "",
        active: true,
        showDivisions: false,
        copyFromSeasonId: "",
      });

      setIsModalOpen(false);
      setMessage(`${res.data.season.name} created`);
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Create error:", err);
    }
  };

  const handleUpdateSeason = async (e) => {
    e.preventDefault();

    try {
      const res = await api.put(`/api/seasonsv2/${updateForm.id}`, updateForm, {
        withCredentials: true,
      });

      const updated = await api.get("/api/seasonsv2", {
        withCredentials: true,
      });

      setSeasons(updated.data.seasons || []);
      setIsUpdateModalOpen(false);
      setMessage(`${res.data.season.name} updated`);
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Update error:", err);
    }
  };

  const handleDeleteSeasons = async () => {
    if (!selectedSeasons.length) {
      return alert("Select at least one season to delete.");
    }

    if (!window.confirm("Delete selected seasons?")) return;

    try {
      await Promise.all(
        selectedSeasons.map((id) =>
          api.delete(`/api/seasonsv2/${id}`, {
            withCredentials: true,
          })
        )
      );

      setSeasons((prev) =>
        prev.filter((season) => !selectedSeasons.includes(season.id))
      );

      setSelectedSeasons([]);
      setMessage("Seasons deleted");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const openUpdateModal = (season) => {
    setUpdateForm({
      id: season.id,
      name: season.name,
      startDate: season.startDate,
      finishDate: season.finishDate,
      active: season.active,
      showDivisions: season.showDivisions,
    });

    setIsUpdateModalOpen(true);
  };

  const handleCheckboxChange = (id) => {
    setSelectedSeasons((prev) =>
      prev.includes(id)
        ? prev.filter((seasonId) => seasonId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedSeasons.length === seasons.length) {
      setSelectedSeasons([]);
    } else {
      setSelectedSeasons(seasons.map((season) => season.id));
    }
  };

  const formatDatePure = (dateStr) => {
    if (!dateStr) return "-";

    const [year, month, day] = dateStr.split("T")[0].split("-");
    return `${month}-${day}-${year}`;
  };

  const toDateInputValue = (dateStr) => {
    if (!dateStr) return "";
    return dateStr.split("T")[0];
  };

  const getEmptyColSpan = () => {
    let count = 9;

    if (isSuperAdmin) count += 2;

    return count;
  };

  if (checkingSport) {
    return null;
  }

  if (!hasSportAssigned) {
    return (
      <div className="seasonAuthPage">
        <div className="seasonAuthPage-sportSetupCard">
          <p className="seasonAuthPage-eyebrow">Sport Setup</p>

          <h2 className="seasonAuthPage-title">Choose Your Sport</h2>

          <p className="seasonAuthPage-sportSetupText">
            Select a sport before creating your first season.
          </p>

          <select
            className="seasonAuthPage-sportSelect"
            value={selectedSportId}
            onChange={(e) => setSelectedSportId(e.target.value)}
          >
            <option value="">Select a sport</option>

            {sports.map((sport) => (
              <option key={sport.id} value={sport.id}>
                {sport.type}
              </option>
            ))}
          </select>

          <button
            className="seasonAuthPage-primaryBtn"
            onClick={handleSaveSport}
            disabled={savingSport || !selectedSportId}
          >
            {savingSport ? "Saving..." : "Continue"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="seasonAuthPage">
      <div className="seasonAuthPage-card">
        <div className="seasonAuthPage-header">
          <div>
            <h2 className="seasonAuthPage-title">Seasons</h2>

            <p className="seasonAuthPage-subtitle">
              Create seasons, manage season dates, and open each season dashboard.
            </p>
          </div>

          <div className="seasonAuthPage-actions">
            <button
              type="button"
              className="seasonAuthPage-primaryBtn"
              onClick={() => setIsModalOpen(true)}
            >
              + Add Season
            </button>

            <button
              type="button"
              className="seasonAuthPage-dangerBtn"
              onClick={handleDeleteSeasons}
              disabled={selectedSeasons.length === 0}
              title={
                selectedSeasons.length === 0
                  ? "Select seasons first"
                  : "Delete selected seasons"
              }
            >
              Delete Selected
              {selectedSeasons.length > 0 && ` (${selectedSeasons.length})`}
            </button>
          </div>
        </div>

        {message && (
          <div className="seasonAuthPage-message">
            {message}
          </div>
        )}

        <div className="seasonAuthPage-tableWrap">
          <table className="seasonAuthPage-table">
            <thead>
              <tr>
                <th className="seasonAuthPage-checkboxCol">
                  <input
                    type="checkbox"
                    checked={
                      seasons.length > 0 &&
                      selectedSeasons.length === seasons.length
                    }
                    onChange={handleSelectAll}
                  />
                </th>

                <th>#</th>
                <th>Name</th>

                {isSuperAdmin && <th>Organization</th>}
                {isSuperAdmin && <th>Sport</th>}

                <th>Start</th>
                <th>Finish</th>
                <th>Active</th>

                <th>Tournament</th>

                <th className="seasonAuthPage-actionCol"></th>
              </tr>
            </thead>

            <tbody>
              {loading ? null : seasons.length === 0 ? (
                <tr>
                  <td
                    colSpan={getEmptyColSpan()}
                    className="seasonAuthPage-emptyCell"
                  >
                    No seasons available
                  </td>
                </tr>
              ) : (
                seasons.map((season, index) => (
                  <tr key={season.id}>
                    <td className="seasonAuthPage-checkboxCol">
                      <input
                        type="checkbox"
                        checked={selectedSeasons.includes(season.id)}
                        onChange={() => handleCheckboxChange(season.id)}
                      />
                    </td>

                    <td>{index + 1}</td>

                    <td>
                      <button
                        type="button"
                        className="seasonAuthPage-nameBtn"
                        onClick={() => navigate(`/dashboard/seasons/${season.id}`)}
                      >
                        {season.name}
                      </button>
                    </td>

                    {isSuperAdmin && (
                      <td>{season.organization?.name || "-"}</td>
                    )}

                    {isSuperAdmin && (
                      <td>{season.sportsv2?.type || "-"}</td>
                    )}

                    <td>{formatDatePure(season.startDate)}</td>
                    <td>{formatDatePure(season.finishDate)}</td>

                    <td>
                      <span
                        className={
                          season.active
                            ? "seasonAuthPage-statusPill seasonAuthPage-statusActive"
                            : "seasonAuthPage-statusPill seasonAuthPage-statusInactive"
                        }
                      >
                        {season.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={
                          season.showDivisions
                            ? "seasonAuthPage-statusPill seasonAuthPage-statusActive"
                            : "seasonAuthPage-statusPill seasonAuthPage-statusOff"
                        }
                      >
                        {season.showDivisions ? "Enabled" : "Disabled"}
                      </span>
                    </td>

                    <td className="seasonAuthPage-actionCol">
                      <button
                        type="button"
                        className="seasonAuthPage-pencilBtn"
                        onClick={() => openUpdateModal(season)}
                      >
                        ✏️
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="seasonAuthPage-modalOverlay">
          <div className="seasonAuthPage-modal">
            <div className="seasonAuthPage-modalHeader">
              <div>
                <h3>Create Season</h3>
                <p>Add a new season for this organization.</p>
              </div>

              <button
                type="button"
                className="seasonAuthPage-closeBtn"
                onClick={() => setIsModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <form
              className="seasonAuthPage-form"
              onSubmit={handleCreateSeason}
            >
              <label>
                Season Name
                <input
                  type="text"
                  value={seasonForm.name}
                  placeholder="Season name"
                  onChange={(e) =>
                    setSeasonForm({
                      ...seasonForm,
                      name: e.target.value,
                    })
                  }
                  required
                />
              </label>

              <div className="seasonAuthPage-formGrid">
                <label>
                  Start Date
                  <input
                    type="date"
                    value={seasonForm.startDate}
                    onChange={(e) =>
                      setSeasonForm({
                        ...seasonForm,
                        startDate: e.target.value,
                      })
                    }
                  />
                </label>

                <label>
                  Finish Date
                  <input
                    type="date"
                    value={seasonForm.finishDate}
                    onChange={(e) =>
                      setSeasonForm({
                        ...seasonForm,
                        finishDate: e.target.value,
                      })
                    }
                  />
                </label>
              </div>

              <div className="seasonAuthPage-checkRow">
                <label>
                  <input
                    type="checkbox"
                    checked={seasonForm.active}
                    onChange={(e) =>
                      setSeasonForm({
                        ...seasonForm,
                        active: e.target.checked,
                      })
                    }
                  />
                  Active Season
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={seasonForm.showDivisions}
                    onChange={(e) =>
                      setSeasonForm({
                        ...seasonForm,
                        showDivisions: e.target.checked,
                      })
                    }
                  />
                  Tournament
                </label>
              </div>

              <div className="seasonAuthPage-modalActions">
                <button type="submit" className="seasonAuthPage-primaryBtn">
                  Create Season
                </button>

                <button
                  type="button"
                  className="seasonAuthPage-secondaryBtn"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isUpdateModalOpen && (
        <div className="seasonAuthPage-modalOverlay">
          <div className="seasonAuthPage-modal">
            <div className="seasonAuthPage-modalHeader">
              <div>
                <h3>Update Season</h3>
                <p>Edit season details and settings.</p>
              </div>

              <button
                type="button"
                className="seasonAuthPage-closeBtn"
                onClick={() => setIsUpdateModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <form
              className="seasonAuthPage-form"
              onSubmit={handleUpdateSeason}
            >
              <label>
                Season Name
                <input
                  type="text"
                  value={updateForm.name}
                  onChange={(e) =>
                    setUpdateForm({
                      ...updateForm,
                      name: e.target.value,
                    })
                  }
                  required
                />
              </label>

              <div className="seasonAuthPage-formGrid">
                <label>
                  Start Date
                  <input
                    type="date"
                    value={
                      updateForm.startDate
                        ? toDateInputValue(updateForm.startDate)
                        : ""
                    }
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        startDate: e.target.value,
                      })
                    }
                  />
                </label>

                <label>
                  Finish Date
                  <input
                    type="date"
                    value={
                      updateForm.finishDate
                        ? toDateInputValue(updateForm.finishDate)
                        : ""
                    }
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        finishDate: e.target.value,
                      })
                    }
                  />
                </label>
              </div>

              <div className="seasonAuthPage-checkRow">
                <label>
                  <input
                    type="checkbox"
                    checked={updateForm.active}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        active: e.target.checked,
                      })
                    }
                  />
                  Active Season
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={updateForm.showDivisions}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        showDivisions: e.target.checked,
                      })
                    }
                  />
                  Tournament
                </label>
              </div>

              <div className="seasonAuthPage-modalActions">
                <button type="submit" className="seasonAuthPage-primaryBtn">
                  Update Season
                </button>

                <button
                  type="button"
                  className="seasonAuthPage-secondaryBtn"
                  onClick={() => setIsUpdateModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SeasonList;